<template>
  <section class="product-table">
    <TableClassic
      :columns="columns"
      :rows="customRows"
      :total-rows="customRows.length"
      :checkboxes="!disableEditing"
      :internal-sort="!noSorting"
      :active-sort="activeSort"
      :allow-product-click="linkLines"
      @sort="$emit('sort', $event)"
    >
      <template #image="{ value, }">
        <img
          :src="defineImgUrl(value)"
          :alt="$t('general.alt-image-produit')"
          :class="{ duplicated: isDuplicated(value), }"
          referrerpolicy="no-referrer"
          @error="imgHasError[value.id] = true"
        />
        <div
          v-if="isDuplicated(value)"
          v-tooltip="{
            content: duplicateTooltip(
              catalogue(value) === 'numerique'
                ? defineOffer(value).ean_lde
                : defineProduct(value).ean
            ),
            html: true,
          }"
          class="duplicate"
        >
          <UilInfoCircle />
        </div>
      </template>
      <template #libelle="{ value, }">
        <div :class="{ 'comment-displayed': value.show_comment, }">
          <h2>
            {{ defineProduct(value).libelle }}
          </h2>
          <p v-if="defineProduct(value)?.reference_interdacta">
            {{ defineProduct(value)?.reference_interdacta }}
          </p>
          <p v-else-if="defineProduct(value)?.ean">
            {{ defineProduct(value)?.ean }}
          </p>
          <p v-if="defineOffer(value) && defineOffer(value).duree_verbose">
            {{ defineOffer(value).duree_verbose }}
          </p>
          <p v-if="defineOffer(value) && !compatibleAdoptant && catalogue(value) === 'numerique'">
            {{ defineOffer(value).adoptant ? $t('offre.adoptant') : $t('offre.non-adoptant') }}
          </p>
          <ul
            v-if="defineOffer(value) && value.gratuites_calculees"
            class="product-lep"
          >
            <li
              v-for="lep in defineOffer(value).livresenplus"
              :key="lep.id"
            >
              <template v-if="lep.id && value.gratuites_calculees[lep.id]">
                {{
                  $t(
                    "produit.x-licence-gratuite",
                    value.gratuites_calculees[lep.id].split(" pour ")[0],
                    { licence: value.gratuites_calculees[lep.id].split(" pour ")[0], }
                  )
                }}
                ({{ lep.article_gratuit_verbose }}
                {{
                  (lep.duree % 12) === 0
                    ? $t("general.x-an", lep.duree / 12, { an: lep.duree / 12, })
                    : $t("general.x-mois", { mois: lep.duree, })
                }})
                {{ $t(
                  "produit.pour-x-achetee",
                  value.gratuites_calculees[lep.id].split(" pour ")[1],
                  { nombre: value.gratuites_calculees[lep.id].split(" pour ")[1], }
                ) }}
              </template>
            </li>
          </ul>
          <div
            v-if="Number(value.remise) === 100 && Number(value.etat) === 20"
            class="demande-refusee"
          >
            <Toast
              variant="warning"
              :show-icon="false"
              hide-close-btn
              :content="$t('produit.votre-etablissement-atteint-quote-licences-ens')"
            />
          </div>
        </div>
        <CommentaireListe
          v-if="value.show_comment"
          :value="value"
          :collaboration="collaboration"
          type-object="ligne_de_liste"
        />
      </template>
      <template #numero-commande="{ value, }">
        <span class="data">
          {{ value.commande }}
        </span>
      </template>
      <template #uai="{ value, }">
        <span class="data">
          {{ value.organisme.uai }}
        </span>
      </template>
      <template #organisme="{ value, }">
        <span
          class="data"
          v-html="value.organisme.nom_complet.replaceAll(' - ', '<br/>')"
        />
        <br />
        <span class="data">
          {{ value.organisme.uai }}
        </span>
      </template>
      <template #numero-devis="{ value, }">
        <span class="data">
          {{ value.devis }}
        </span>
      </template>
      <template #commande-date="{ value, }">
        <span class="data">
          {{ $moment(value.commande_date_creation).format($t("global.format-date")) }}
        </span>
      </template>
      <template #devis-date="{ value, }">
        <span class="data">
          {{ $moment(value.devis_date_creation).format($t("global.format-date")) }}
        </span>
      </template>
      <template #devis-se-etat="{ value, }">
        <span class="data">
          {{ value.devis_se_etat }}
        </span>
      </template>
      <template #prix="{ value, }">
        <template v-if="parseFloat(value.remise)">
          <Badge
            variant="remise"
            :title="`${value.remise}&nbsp;%`"
          />
          <span
            class="prix-sans-remise"
            v-html="definePrixNonRemise(value).ttc !== definePrixNonRemise(value).ht
              ? $t('prix.ttc', { prix: moneyFilter(definePrixNonRemise(value).ttc), })
              : $t('prix.ht', { prix: moneyFilter(definePrixNonRemise(value).ht), })
            "
          />
        </template>

        <template v-if="definePrixRemise(value)">
          <strong
            v-if="definePrixRemise(value).ttc !== definePrixRemise(value).ht"
            class="all-taxes"
            v-html="$t('prix.ttc', { prix: moneyFilter(definePrixRemise(value).ttc), })"
          />
          <strong
            class="duty-free"
            v-html="$t('prix.ht', { prix: moneyFilter(definePrixRemise(value).ht), })"
          />
        </template>
      </template>
      <template #quantite="{ value, }">
        <InputQuantity
          v-model="value.quantite"
          inline
          :class="{ edited: value.quantiteModifiee, }"
          hide-buttons
          :disabled="disableEditing || !isOfferAvailable(value.offre)"
          :status="!disableEditing && catalogue(value) === 'numerique' && value.quantite < value.offre.quantite_min
            ? 'warning'
            : 'default'
          "
          :min="!isLogged && catalogue(value) === 'numerique'
            ? (value.offre.quantite_min || 1)
            : quantityMin
          "
          :max="quantityMax"
          @click.stop
          @update:modelValue="changeQuantity(value, $event)"
        />
      </template>
      <template #sous-total="{ value, }">
        <strong
          v-if="value.total_ligne_ttc !== value.total_ligne_ht"
          class="all-taxes"
          v-html="$t('prix.ttc', { prix: moneyFilter(value.total_ligne_ttc), })"
        />
        <span
          class="duty-free"
          v-html="$t('prix.ht', { prix: moneyFilter(value.total_ligne_ht), })"
        />
      </template>
      <template #type_ldc="{ value, }">
        <div class="data text-small">
          {{ value.type_ldc }}
        </div>
      </template>
      <template #statut="{ value, }">
        <template v-if="isCommande ? value.order_label : defineStatusFormated(value)">
          <div :class="{ 'status-epuise': defineStatusTooltip(value), }">
            <TimelineLabel
              :label="isCommande ? value.order_label : defineStatusFormated(value).label"
              :status="isCommande ? defineOrderStatus(value) : defineStatusFormated(value).statut"
              small
            />
            <UilInfoCircle
              v-if="defineStatusTooltip(value)"
              v-tooltip="{
                content: defineStatusTooltip(value),
                html: true,
              }"
              class="statut-info"
            />
          </div>
          <p
            v-if="value.date_livraison_estimee"
            v-html="`${$t('produit.livraison-estimee')} ${value.date_livraison_estimee}`"
          />
        </template>
      </template>
      <template #actions="{ value, }">
        <ButtonClassic
          v-if="isLogged && !isCommande && !isDevis && value.id"
          v-tooltip="{
            content: $t('action.ajouter-un-commentaire'),
            delay: { show: 800, },
          }"
          variant="special"
          color="primary"
          icon="left"
          size="small"
          @click="displayComment(value)"
        >
          <template #left-icon>
            <UilCommentAltLines v-if="value.commentaire" />
            <UilCommentAlt v-else />
          </template>
        </ButtonClassic>
        <ButtonClassic
          v-if="!noDeletionPossible"
          v-tooltip="{
            content: $t('action.mettre-poubelle'),
            delay: { show: 800, },
          }"
          variant="special"
          color="primary"
          icon="left"
          size="small"
          @click="$emit('delete-line', value)"
        >
          <template #left-icon>
            <UilTrashAlt />
          </template>
        </ButtonClassic>
        <ButtonClassic
          v-if="links && value.link && value.link.name"
          v-tooltip="{
            content: value.link.tooltip ?? $t('action.voir-detail'),
            delay: { show: 800, },
            html: true,
          }"
          variant="special"
          color="primary"
          icon="left"
          size="small"
          :class="{ 'always-visible': !value.link.showOnHoverOnly, }"
          @click="$router.push({ name: value.link.name, params: value.link.params ?? {}, })"
        >
          <template
            v-if="value.link.icon"
            #left-icon
          >
            <component
              :is="value.link.icon"
              size="16"
            />
          </template>
        </ButtonClassic>
      </template>

      <template
        v-if="isLogged"
        #additional-line="{ value, }"
      >
        <div
          v-if="value.raison"
          class="minima"
        >
          <p class="text-medium">
            {{
              $t(
                "ask-licence.nombre-licence-enseignant-gratuites-x",
                value.gratuites,
                { nombre: value.gratuites, }
              )
            }}
          </p>
          <div v-if="value.quantite > value.gratuites">
            <br /> <br />
            <p>
              {{ $t('ask-licence.text-raison-licence-supp') }}{{ "\xa0" }}:
            </p>
            <ul>
              <li>
                <ButtonToggle
                  v-model="value.raison"
                  name="effectif_reduit"
                  input-value="effectif_reduit"
                  label-left
                  :label="$t('ask-licence.raison-effectif')"
                />
              </li>
              <li>
                <ButtonToggle
                  v-model="value.raison"
                  name="plsr_enseignants"
                  input-value="plsr_enseignants"
                  label-left
                  :label="$t('ask-licence.raison-plsr-enseignants')"
                />
              </li>
              <li>
                <ButtonToggle
                  v-model="value.raison"
                  name="handicap"
                  input-value="handicap"
                  label-left
                  :label="$t('ask-licence.raison-handicap')"
                />
              </li>
            </ul>
          </div>
        </div>
        <div v-else>
          <p :class="{ 'label-warning': !value.raison_minima, }">
            {{ $t('produit.informations-justifier-commandes-sous-minima') }}{{ "\xa0" }}:
          </p>
          <ul>
            <li>
              <ButtonToggle
                v-model="value.raison_minima"
                name="effectif_reduit"
                input-value="effectif_reduit"
                label-left
                :label="$t('produit.classe-effectif-inferieur-quantite-minimale')"
                @update:modelValue="changeMinima(value, $event)"
              />
            </li>
            <li>
              <ButtonToggle
                v-model="value.raison_minima"
                name="situation_handicap"
                input-value="situation_handicap"
                label-left
                :label="$t('produit.atteste-commander-minima-handicap')"
                @update:modelValue="changeMinima(value, $event)"
              />
            </li>
            <li>
              <p
                v-if="!value.complements?.commandes?.length && !value.complements?.devis?.length"
                class="text-small italic no-complement"
              >
                {{ $t("produit.aucune-commande-detectee") }}
                <UisExclamationTriangle size="16" />
              </p>
              <ButtonToggle
                v-model="value.raison_minima"
                name="complement"
                input-value="complement"
                label-left
                :label="$t('produit.jatteste-commander-dessous-minima')"
                :disabled="!value.complements?.commandes?.length && !value.complements?.devis?.length"
                @update:modelValue="changeMinima(value, $event)"
              />
              <div
                v-if="value.complements?.commandes?.length"
                class="complement"
              >
                <div class="bold">
                  {{ $t("produit.commandes-reference-annee-scolaire") }}
                </div>
                <ul>
                  <li
                    v-for="cmd in value.complements.commandes"
                    :key="cmd.id"
                  >
                    {{
                      $t("minima.complement-commande", cmd.quantite, {
                        id: cmd.id_4d,
                        date: $moment(cmd.date).format($t("global.format-date")),
                        qte: cmd.quantite,
                      })
                    }}
                  </li>
                </ul>
              </div>
              <div
                v-if="value.complements?.devis?.length"
                class="complement"
              >
                <div class="bold">
                  {{ $t("produit.devis-reference-annee-scolaire") }}
                </div>
                <ul>
                  <li
                    v-for="dev in value.complements.devis"
                    :key="dev.id"
                  >
                    {{
                      $t("minima.complement-devis", dev.quantite, {
                        id: dev.id_4d,
                        date: $moment(dev.date).format($t("global.format-date")),
                        qte: dev.quantite,
                      })
                    }}
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </template>
    </TableClassic>
  </section>
</template>

<script>
import { mapGetters } from "vuex";

import {
  InputQuantity,
  ButtonClassic,
  ButtonToggle,
  moneyFilter,
  Toast,
} from "@lde/core_lde_vue";
import {
  UilTrashAlt, UilCommentAlt, UilCommentAltLines, UilInfoCircle,
} from "@lde/vue-unicons";
import {
  UisExclamationTriangle,
} from "@lde/vue-unicons-solid";

import TableClassic from "@/components/table/TableClassic.vue";
import TimelineLabel from "@/components/timeline/TimelineLabel.vue";
import Badge from "@/components/Badge.vue";
import mixinProductUtils from "@/mixins/mixinProductUtils";
import mixinDisponibilite from "@/mixins/mixinDisponibilite";
import CommentaireListe from "@/components/listes_devis_commandes/CommentaireListe.vue";
import config from "@/config";
import codesEtatLigneEnErreur from "@/utils/etatsProduit";

/**
 * Affiche les produits dans un tableau spécifique.
 */
export default {
  name: "ProductTable",
  components: {
    InputQuantity,
    ButtonClassic,
    ButtonToggle,
    TableClassic,
    TimelineLabel,
    Badge,
    UilTrashAlt,
    UilCommentAlt,
    UisExclamationTriangle,
    CommentaireListe,
    UilCommentAltLines,
    UilInfoCircle,
    Toast,
  },
  mixins: [mixinDisponibilite, mixinProductUtils],
  props: {
    /**
     * Données à afficher dans le tableau.
     */
    rows: {
      type: Array,
      required: true,
    },
    /**
     * Indique les doublons de produits qui se trouvent déjà dans les devis et commandes en cours.
     */
    productDuplicates: {
      type: Array,
      default: () => [],
    },
    /**
     * Contexte d'affichage du tableau.
     * "liste", "devis" et "commande" affichent les lignes d'un seul objet (fiche, panier).
     * Les valeurs "lignes-*" affichent des lignes provenant de plusieurs objets
     * (vues LDC/LDD, résultats de recherche) et ajoutent les colonnes de l'objet d'origine.
     */
    context: {
      type: String,
      validator: (value) => [
        "liste",
        "devis",
        "commande",
        "lignes-liste",
        "lignes-devis",
        "lignes-commande",
      ].includes(value),
      default: "liste",
    },
    /**
     * Empêche la modification de la quantité et cache la checkbox.
     */
    disableEditing: {
      type: Boolean,
      default: false,
    },
    /**
     * Empêche la suppression d'une ligne.
     */
    noDeletionPossible: {
      type: Boolean,
      default: false,
    },
    /**
     * Désactiver le tri interne du tableau.
     */
    noSorting: {
      type: Boolean,
      default: false,
    },
    /**
     * Active ou non les liens sur les lignes.
     */
    links: {
      type: Boolean,
      default: false,
    },
    /**
     * Variante d'affichage des lignes du tableau si le devis est expiré.
     */
    expired: {
      type: Boolean,
      default: false,
    },
    /**
     * Quantité minimale pour l'input de la ligne.
     */
    quantityMin: {
      type: Number,
      default: 1,
    },
    /**
     * Quantité maximale pour l'input de la ligne.
     */
    quantityMax: {
      type: Number,
      default: 9999,
    },
    /**
     * Clé et sens du tri.
     */
    activeSort: {
      type: Object,
      default: () => ({}),
    },
    collaboration: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Déclenché à la modification d'une ligne.
     * @param {Object} ligne Ligne à modifier.
     */
    "change-line",
    /**
     * Déclenché au clic sur le bouton de suppression.
     * @param {Object} ligne Ligne à modifier.
     */
    "delete-line",
    /**
     * Au choix de la raison du minima.
     */
    "select-minima",
    /**
     * Déclenché lors du tri.
     */
    "sort",
  ],
  data() {
    return {
      debounceTimer: null,
    };
  },
  computed: {
    ...mapGetters(["compatibleAdoptant", "isHorsMarche", "hasPerm", "isLogged", "isMaitreComptaRegion"]),
    linkLines() {
      return this.$route.name !== "listes_devis_devis_ldd";
    },
    /**
     * Indique si les lignes affichées sont des lignes de commande.
     * @returns {Boolean} Vrai si le contexte est une commande.
     */
    isCommande() {
      return ["commande", "lignes-commande"].includes(this.context);
    },
    /**
     * Indique si les lignes affichées sont des lignes de devis.
     * @returns {Boolean} Vrai si le contexte est un devis.
     */
    isDevis() {
      return ["devis", "lignes-devis"].includes(this.context);
    },
    columns() {
      const activateListe = this.context === "lignes-liste";
      const activateDevis = this.context === "lignes-devis";
      const activateCommande = this.context === "lignes-commande";
      const isNumerique = this.catalogue(this.rows[0]) === "numerique";

      let columns = [
        {
          key: "image",
          name: "",
          notSortable: true,
        },
        {
          key: "libelle",
          classes: ["fill-50"],
          name: this.$t("liste.titre-et-ean"),
          sortKey: "libelle",
          notSortable: this.noSorting && !activateDevis && !activateCommande && !activateListe,
        },
      ];

      if (activateCommande) {
        if (this.isMaitreComptaRegion) {
          columns.push(
            {
              key: "uai",
              sortKey: "uai",
              name: this.$t("general.uai"),
            },
            {
              key: "organisme",
              sortKey: "organisme",
              name: this.$t("general.etablissement"),
            },
          );
        }

        columns.push(
          {
            key: "numero-commande",
            sortKey: "numero_commande",
            name: this.$t("commande.commande"),
          },
          {
            key: "commande-date",
            sortKey: "date_commande",
            name: this.$t("commande.date-commande"),
          },
        );
      }

      if (activateDevis) {
        if (this.isMaitreComptaRegion) {
          columns.push(
            {
              key: "uai",
              sortKey: "uai",
              name: this.$t("general.uai"),
            },
            {
              key: "organisme",
              sortKey: "organisme",
              name: this.$t("general.etablissement"),
            },
          );
        }

        columns.push(
          {
            key: "numero-devis",
            sortKey: "numero_devis",
            name: this.$t("devis.devis"),
          },
          {
            key: "devis-date",
            sortKey: "date_devis",
            name: this.$t("devis.date-devis"),
          },
        );
      }

      if ((activateDevis) && !this.isHorsMarche) {
        columns.push({
          key: "devis-se-etat",
          sortKey: "devis_se_etat",
          name: this.$t("devis.statut-du-devis"),
        });
      }

      columns = [
        ...columns,
        {
          key: "prix",
          name: this.$t("prix.prix"),
          notSortable: true,
        },
        {
          key: "quantite",
          name: this.$t("general.quantite"),
          notSortable: this.noSorting,
        },
      ];

      if (this.isCommande) {
        if (isNumerique) {
          columns.push({
            key: "type_ldc",
            name: this.$t("general.type-ldc"),
            notSortable: this.noSorting,
          });
        }
      } else {
        columns = [
          ...columns,
          {
            key: "sous-total",
            name: this.$t("general.sous-total"),
            sortKey: "total_ligne_ht",
            notSortable: this.noSorting,
          },
        ];
      }

      columns = [
        ...columns,
        {
          key: "statut",
          name: this.$t("general.statut"),
          notSortable: this.noSorting && !activateListe,
        },
      ];

      if (!this.noDeletionPossible || this.links) {
        columns = [
          ...columns,
          {
            key: "actions",
            notSortable: true,
          },
        ];
      }

      return columns;
    },
    noImgName() {
      const firstLine = this.rows[0];
      let type = "";

      if (firstLine.manuel_numerique) {
        type = "numerique";
      } else if (firstLine.article) {
        type = "papier";
      } else if (firstLine.fourniture) {
        type = "fourniture";
      } else {
        type = "image";
      }

      return type;
    },
    customRows() {
      return this.rows.map((row) => {
        row._additional = (
          this.isLogged
          && !this.disableEditing
          && (row._force_additional
            || (this.catalogue(row) === "numerique"
              && row.quantite < row.offre.quantite_min
              && row.quantite > 0
            )
          )
        );

        row._variant = this.expired ? "dashed" : row._variant;
        return row;
      });
    },
  },
  methods: {
    moneyFilter,
    /**
     * Définit le prix non remisé dd'une ligne.
     * @param {Object} ligne Infos de la lignes.
     * @returns {Object} Prix TTC et HT de l'offre.
     */
    definePrixNonRemise(ligne) {
      return {
        ttc: ligne.liste ? ligne.offre.prix_editeur : ligne.prix_editeur_ttc,
        ht: ligne.liste ? ligne.offre.prix_ht : ligne.prix_editeur_ht,
      };
    },
    /**
     * Définit le prix remisé d'une ligne.
     * @param {Object} ligne Infos de la lignes.
     * @returns {Object|Boolean} Prix TTC et HT de l'offre ou false si aucun prix défini.
     */
    definePrixRemise(ligne) {
      // On passe par un computed pour avoir un nom plus compréhensible par rapport au reste.
      if (ligne.prix_unitaire_ttc == null && ligne.prix_unitaire_ht == null) {
        return false;
      }

      return {
        ttc: ligne.prix_unitaire_ttc,
        ht: ligne.prix_unitaire_ht,
      };
    },
    /**
     * Change la quantité d'une ligne après un délai.
     * @param {Object} ligne Infos de la ligne.
     * @param {Number} val Nouvelle quantité.
     */
    changeQuantity(ligne, val) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        ligne.quantite = val;
        this.$emit("change-line", ligne);
      }, config.api.debounceDelay);
    },
    /**
     * Définit le statut d'une ligne.
     * @param {Object} ligne Infos de la lignes.
     * @returns {Object} Couleur et label du statut.
     */
    defineStatusFormated(ligne) {
      const obj = {
        1: { label: this.$t("produit.disponible"), statut: "valid" },
        2: { label: this.$t("produit.a-paraitre"), statut: "warning" },
        3: { label: this.$t("produit.en-cours-de-reimpression"), statut: "warning" },
        4: { label: this.$t("produit.non-disponible-provisoirement"), statut: "warning" },
        5: { label: this.$t("produit.changement-de-distributeur"), statut: "warning" },
        6: { label: this.$t("produit.arret-definitif-de-commercialisation"), statut: "error" },
        7: { label: this.$t("produit.manque-sans-date"), statut: "error" },
        8: { label: this.$t("produit.a-reparaitre"), statut: "warning" },
        9: { label: this.$t("produit.non-disponible-a-long-terme"), statut: "error" },
        13: { label: this.$t("produit.nous-recherchons-cet-article"), statut: "error" },
        97: { label: this.$t("produit.disponible-jusqu-a-epuisement-des-stocks"), statut: "valid" },
        98: { label: this.$t("produit.retire-du-catalogue-editeur"), statut: "error" },
      };

      return ligne.offre?.code_disponibilite ? obj[ligne.offre.code_disponibilite] : {};
    },
    /**
     * Définit le statut affiché pour une ligne de commande.
     * @param {Object} ligne Infos de la ligne.
     * @returns {String|undefined} Statut du TimelineLabel ou undefined pour garder celui par défaut.
     */
    defineOrderStatus(ligne) {
      if (ligne.order_status) {
        return ligne.order_status;
      }
      if (codesEtatLigneEnErreur.includes(Number(ligne.etat))) {
        return "error";
      }
      return undefined;
    },
    /**
     * Définit le contenu du tooltip explicitant l'arrêt définitif de commercialisation d'une ligne.
     * @param {Object} ligne Infos de la ligne.
     * @returns {String|null} Tooltip de l'état ou null s'il n'y a rien à signaler.
     */
    defineStatusTooltip(ligne) {
      if (Number(ligne.etat) !== 6) {
        return null;
      }
      return ligne.order_tooltip || this.defineStatusFormated(ligne)?.label || null;
    },
    /**
     * Change la raison du minima d'une ligne.
     * @param {Object} ligne Infos de la ligne.
     * @param {String} val raison du minima.
     */
    changeMinima(ligne, val) {
      ligne.raison_minima = val;
      this.$emit("change-line", ligne);
    },
    displayComment(ligne) {
      ligne.show_comment = !ligne.show_comment;
    },
    isDuplicated(value) {
      return this.productDuplicates.find(
        (p) => p.ean === (
          this.catalogue(value) === "numerique"
            ? this.defineOffer(value).ean_lde
            : this.defineProduct(value).ea
        ),
      );
    },
    duplicateTooltip(ean) {
      let res = "";

      const dataDupli = this.productDuplicates.find((p) => p.ean === ean);

      if (dataDupli?.devis?.length > 0) {
        res += `${this.$t("produit.doublon-deja-present-devis")}`;
        res += "<br/>";
        dataDupli.devis.forEach((devis) => {
          res += `<a href='${this.$router.resolve({
            name: "listes_devis_devis_item",
            params: { id: devis.id },
          }).href}'>${this.$t("devis.numero", 1, { id_devis: devis.id_4d })}</a>`;
          res += "<br/>";
        });
      }

      if (dataDupli?.commandes?.length > 0) {
        if (res !== "") res += "<br/>";
        res += `${this.$t("produit.doublon-deja-present-commande")}`;
        res += "<br/>";
        dataDupli.commandes.forEach((commande) => {
          res += `<a href='${this.$router.resolve({
            name: "commandes_factures_commandes_item",
            params: { id: commande.id },
          }).href}'>${this.$t("commande.numero", 1, { id_commande: commande.id_4d })}</a>`;
          res += "<br/>";
        });
      }

      return res;
    },
  },
};
</script>

<style lang="scss">
@use "~@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;
@use "@/assets/styles/components/table/product_table.scss";

.product-table tbody .libelle .comment-displayed {
  h2 {
    font-size: 1.2rem;
    line-height: 2rem;
  }

  p {
    font-size: 1rem;
    line-height: 1.2rem;
  }
}

.product-table .organisme {
  // min-width: 25rem;
  max-width: 25rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.demande-refusee .toast {
  padding: var.$space-x-tiny;
  display: flex;
  align-items: stretch;
  width: max-content;
  text-wrap: wrap;

  .status-bar {
    align-self: stretch;
    height: unset;
  }
}
</style>
